import React, { useState } from 'react';
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CDataTable,
  CButton,
  CCardHeader,
  CNav,
  CNavItem,
  CNavLink,
} from '@coreui/react';
import _t from 'counterpart';
import CIcon from '@coreui/icons-react';
import { useMutation, useQuery } from 'react-query';
import { deleteGroup, loadGroups, loadMt5Groups } from '../../services/BackendService';
import { GroupPlatform, IGroup, IMt5Group } from './types';
import CreateGroupModal from './CreateGroupModal';
import CloneGroupModal from './CloneGroupModal';
import PageLayout from '../../components/PageLayout';
import Error from '../../components/Error';
import Loading from '../../components/Loading';
import { useHistory } from 'react-router';
import { extractErrorMessage, isAdmin } from '../../helpers';
import ConfirmationModal from '../../components/ConfirmationModal';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../helpers/customHooks';

const GroupsPage = () => {
  const [addGroupShow, setAddGroupShow] = useState<boolean>(false);
  const [cloneGroupShow, setCloneGroupShow] = useState<boolean>(false);
  const [groupToClone, setGroupToClone] = useState<IGroup | null>(null);
  const [toDeleteGroup, setToDeleteGroup] = useState<IGroup | null>(null);

  const [platform, setPlatform] = useState<GroupPlatform>(GroupPlatform.Wintrado);
  const isMt5 = platform === GroupPlatform.MT5;

  // Two different sources, so two queries: WINTRADO groups are ours (local table), MT5 groups are
  // the MT5 server's and are fetched through the bridge. Only the active tab's query runs.
  const loadGroupsQuery = useQuery(['groups', GroupPlatform.Wintrado], () => loadGroups(GroupPlatform.Wintrado), {
    enabled: !isMt5,
  });
  const loadMt5GroupsQuery = useQuery(['mt5-groups'], () => loadMt5Groups(), { enabled: isMt5 });
  const activeQuery = isMt5 ? loadMt5GroupsQuery : loadGroupsQuery;
  const history = useHistory();

  const deleteGroupMutation = useMutation((id: number) => deleteGroup(id), {
    onSuccess: () => {
      toast.success(_t('groups.group-successfully-deleted'));
      setToDeleteGroup(null);
      loadGroupsQuery.refetch();
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const closeAddGroup = (refetchOnClose?: boolean) => {
    setAddGroupShow(false);
    if (refetchOnClose) {
      loadGroupsQuery.refetch();
    }
  };

  const closeCloneGroup = (refetchOnClose?: boolean) => {
    setCloneGroupShow(false);
    setGroupToClone(null);
    if (refetchOnClose) {
      loadGroupsQuery.refetch();
    }
  };

  const onGroupEditClicked = (group: IGroup) => {
    history.push(`/groups/${encodeURIComponent(group.id)}`);
  };

  const onGroupCloneClicked = (group: IGroup) => {
    setGroupToClone(group);
    setCloneGroupShow(true);
  };

  const onErrorRetry = () => {
    activeQuery.refetch();
  };

  const openDeleteInstrumentModal = (group: IGroup) => {
    setToDeleteGroup(group);
  };

  const closeDeleteInstrumentModal = () => {
    setToDeleteGroup(null);
  };

  const invokeDeleteMutation = () => {
    deleteGroupMutation.mutate(toDeleteGroup!.id);
  };

  const roles = useAppSelector((state) => state.user.roles);
  const userIsAdmin = isAdmin(roles);

  if (activeQuery.isLoading || activeQuery.isIdle) {
    return <Loading />;
  }

  if (activeQuery.isError) {
    return <Error onRetry={onErrorRetry} />;
  }

  return (
    <PageLayout title={_t.translate('groups.title')}>
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader className="pb-0">
              <div className="float-right">
                {/* MT5 groups are created and configured on the MT5 server, not here. */}
                {userIsAdmin && !isMt5 && (
                  <CButton className="mr-2 header-button-add" onClick={() => setAddGroupShow(true)} />
                )}
              </div>
              <CNav variant="tabs">
                <CNavItem>
                  <CNavLink active={!isMt5} onClick={() => setPlatform(GroupPlatform.Wintrado)}>
                    {_t('groups.tab-wintrado')}
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink active={isMt5} onClick={() => setPlatform(GroupPlatform.MT5)}>
                    {_t('groups.tab-mt5')}
                  </CNavLink>
                </CNavItem>
              </CNav>
            </CCardHeader>
            <CCardBody>
              {isMt5 && <p className="text-muted small">{_t('groups.mt5-read-only')}</p>}
              <CDataTable
                fields={
                  isMt5
                    ? [
                        { key: 'group', label: _t('groups.name'), sorter: true },
                        { key: 'currency', label: _t('groups.currency'), sorter: true },
                        { key: 'company', label: _t('groups.company'), sorter: true },
                      ]
                    : [
                        { key: 'name', label: _t('groups.name'), sorter: true },
                        { key: 'leverage', label: _t('groups.leverage'), sorter: true },
                        { key: 'actions', label: _t('global.actions'), sorter: false },
                      ]
                }
                scopedSlots={{
                  group: (group: IMt5Group) => <td>{group.group}</td>,
                  currency: (group: IMt5Group) => <td>{group.currency}</td>,
                  company: (group: IMt5Group) => <td>{group.company}</td>,
                  name: (group: IGroup) => <td>{group.name}</td>,
                  leverage: (group: IGroup) => <td>{group.leverage}</td>,
                  actions: (group: IGroup) => (
                    <td>
                      <CButton size="sm" className="mr-1" color="primary" onClick={() => onGroupEditClicked(group)}>
                        <CIcon name="cil-pencil" size="sm" />
                      </CButton>
                      <CButton
                        className="mr-1"
                        size="sm"
                        color="primary"
                        onClick={() => onGroupCloneClicked(group)}
                        title="Clone group"
                      >
                        <CIcon name="cil-clone" size="sm" />
                      </CButton>
                      {userIsAdmin && (
                        <CButton
                          className="mr-2"
                          size="sm"
                          color="danger"
                          onClick={() => openDeleteInstrumentModal(group)}
                        >
                          <CIcon name="cil-trash" size="sm" />
                        </CButton>
                      )}
                    </td>
                  ),
                }}
                items={(activeQuery.data as Array<any>) || []}
                loading={activeQuery.isLoading}
                sorterValue={{ column: isMt5 ? 'group' : 'name', asc: false }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CreateGroupModal show={addGroupShow} hideAddGroup={closeAddGroup} />
      <CloneGroupModal show={cloneGroupShow} hideCloneGroup={closeCloneGroup} groupToClone={groupToClone} />
      <ConfirmationModal
        id={toDeleteGroup?.id.toString() ?? ''}
        show={toDeleteGroup !== null}
        onConfirm={invokeDeleteMutation}
        title={_t('groups.delete-group')}
        error=""
        buttonColor="danger"
        hidePanel={closeDeleteInstrumentModal}
        withMutation={false}
        isLoading={deleteGroupMutation.isLoading}
      />
    </PageLayout>
  );
};

export default GroupsPage;
